import React, { Component } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import * as R from 'ramda'

import Select from '../../form/Select'
import Checkbox from '../../form/Checkbox'
import Panel from '../../../UniformComponents/Panel'
import Spinner from '../../../UniformComponents/Spinner'
import Button from '../../../UniformComponents/Button'
import Input from '../../form/Input'
import HoverInfo from '../../../UniformComponents/HoverInfo'
import Carousel from '../../../UniformComponents/Carousel'
import Typography from '../../../UniformComponents/Typography'
import Anchor from '../../../UniformComponents/Anchor'
import { Survey } from '../../../Models/Survey'

@observer
export default class SurveyCreate extends Component {
    async componentDidMount() {
        const { wizardStore } = this.props
        await wizardStore.initSurveyCreate()
    }

    renderBlankSurvey = () => {
        const { wizardStore } = this.props
        const {
            surveyType, isRapidSurvey, surveyOptionEnum, surveyOptions,
            appStore, setDefaultLocale,
        } = wizardStore
        const blankSurveyId = surveyOptionEnum.BLANK_SURVEY

        if (surveyType === 'EM' || isRapidSurvey) return null

        return (
            (
                <div className="create-link-wrapper">
                    <h4>{i18n.t('surveyWizard.divider_or')}</h4>
                    <Panel
                        cssClass={`survey-option overflow-visible ${surveyOptions[blankSurveyId].selected ? 'selected' : ''}`}
                        onClick={() => wizardStore.setTemplate(true, blankSurveyId)}
                    >
                        <div className="icon-circle small">
                            <i className="option-icon fa-light fa-clipboard-list-check" />
                        </div>
                        <p className="option-name info">
                            {i18n.t('sw_blanksurveytext')}
                            <HoverInfo
                                cssClass="hover-info w-450"
                                desc={i18n.t('sw_blanksurveytext.tooltip')}
                                dir="right"
                            >
                                <i className="info-icon fa-solid fa-circle-question" />
                            </HoverInfo>
                        </p>

                        {surveyOptions[blankSurveyId].selected && (
                            <Select
                                cssClass="option-select"
                                label="Language"
                                tooltipTitle={i18n.t('blankSurvey.languageSelectionTitle')}
                                tooltipText={i18n.t(`${isRapidSurvey ? 'rapid' : 'pro'}Survey.languageSelectionBody`)}
                                options={Object.entries(appStore.clientLocales)
                                    .map((loc) => ({ label: loc[1], value: loc[0] })).reverse()}
                                onBlur={setDefaultLocale}
                            />
                        )}
                    </Panel>
                </div>
            )
        )
    }

    renderNoTemplates = () => (
        <div className="noTemplates">
            <img src={`${process.env.CDN_URL}/images/noTemplates.png`} />
            <br />
            <Typography type="h3">
                {i18n.t('views.survey.wizard.R.stage.3.info.noTemplate.title')}
            </Typography>
            <br />
            <Typography type="p">
                {i18n.t('views.survey.wizard.R.stage.3.info.noTemplate.desc')}
            </Typography>
        </div>
    )

    getPreviewLink = (publicId) => {
        const { wizardStore } = this.props
        const { survey } = wizardStore
        const { metadata: { defaultLocale } } = survey
        return Survey.getPreviewLinkNew(true, true, defaultLocale, publicId)
    }

    mapTemplateOptions = (source, option) => {
        if (Array.isArray(option.options)) return option.options.flatMap((o) => this.mapTemplateOptions(source, o))

        const { value } = option
        return [{
            ...option,
            id: source.id,
            selected: source.selected?.value === value,
        }]
    }

    renderRapidTemplates = () => {
        const { wizardStore } = this.props
        const { surveyDetails, surveyOptions, isRapidSurvey, setTemplate, templateLocales } = wizardStore

        if (!isRapidSurvey) return null

        const options = surveyOptions.filter(({ isHide }) => !isHide())
            .flatMap((option) => (
                option.options.flatMap((subOption) => (
                    this.mapTemplateOptions(option, subOption)
                ))
            ))

        return options.length > 0
            ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center', justifyContent: 'center' }}>
                    {options.map((option, ind) => {
                        const { label, value, publicId, selected, id } = option
                        const selectLabel = selected
                            ? 'views.survey.wizard.R.stage.3.template.selected'
                            : 'views.survey.wizard.R.stage.3.template.choose'
                        return (
                            <Panel
                                key={value}
                                cssClass={`survey-option rapidSurvey overflow-visible ${selected ? 'selected' : ''}`}
                            >
                                <div className="icon-circle green">
                                    <i className="option-icon fa-light fa-table-layout" />
                                </div>
                                <p>
                                    {label}
                                </p>
                                <div className="buttonGroup">
                                    <Anchor
                                        isExternal
                                        cssClass="option-btn uc-button tertiary regular w-100"
                                        href={this.getPreviewLink(publicId)}
                                        target="_blank"
                                    >
                                        {i18n.t('preview')}
                                    </Anchor>

                                    {!selected
                                        ? (
                                            <Button
                                                cssClass={`option-btn regular w-100 ${selected ? 'secondary' : 'tertiary'}`}
                                                label={i18n.t(selectLabel)}
                                                onClick={() => setTemplate({ label, value }, id)}
                                            />
                                        ) : (
                                            <Select
                                                label="Language"
                                                className="exploriDropdown"
                                                tooltipTitle={i18n.t('rapidSurvey.languageSelectionTitle')}
                                                tooltipText={i18n.t('rapidSurvey.languageSelectionBody')}
                                                tooltipDir={(ind + 1) % 4 === 0 ? 'left' : 'right'}
                                                onBlur={wizardStore.setDefaultLocale}
                                                options={surveyDetails.fetchData.locales.filter((loc) => templateLocales.includes(loc.value))}
                                                styles={{
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />
                                        )}
                                </div>
                            </Panel>
                        )
                    })}
                </div>
            ) : this.renderNoTemplates()
    }

    renderTemplates = () => {
        const { wizardStore } = this.props
        const { surveyOptions, isRapidSurvey, clientLocales, templateLocales, surveyLoading } = wizardStore

        if (isRapidSurvey) return null

        const showTemplateLangWarning = !clientLocales.length

        return (
            surveyOptions.map((option, i) => (
                !option.isHide() ? (
                    <Panel
                        cssClass={`survey-option overflow-visible ${surveyOptions[i].selected ? 'selected' : ''}`}
                    >
                        <div className={`icon-circle ${option.color}`}>
                            <i className={`option-icon ${option.icon}`} />
                        </div>
                        <p className={`option-name ${[0, 1, 2].includes(option.id) ? 'info' : ''}`}>
                            {option.name}
                            {option.infoDescription ? (
                                <HoverInfo
                                    cssClass="hover-info w-300"
                                    desc={option.infoDescription}
                                    dir="right"
                                >
                                    <i className="info-icon fa-solid fa-circle-question" />
                                </HoverInfo>
                            ) : null}
                        </p>
                        {R.hasPath(['btnLabel'], option) && (
                            <Button cssClass="option-btn primary regular" onClick={option.onSelect}>
                                {i18n.t(option.btnLabel)}
                            </Button>
                        )}
                        {R.hasPath(['surveyUrl'], option) && (
                            <Input cssClass="option-input" name="surveyLink" value={option.surveyUrl} inputFieldDisabled />
                        )}
                        {R.hasPath(['options'], option) && (
                            <Select
                                cssClass="option-select"
                                placeholder={i18n.t('views.survey.wizard.CE.stage.3.template.placeholder')}
                                options={option.options}
                                value={option.selected}
                                onBlur={(obj) => { option.onSelect(obj, option.id) }}
                                isSearchable
                            />
                        )}
                        {surveyOptions[i].selected && showTemplateLangWarning && !surveyLoading && (
                            <div className="language-warning">
                                <Typography type="tiny">
                                    {i18n.t('surveyWizard.surveyCreate.languageWarning1', {
                                        1: i === 2 ? 'survey' : 'template',
                                    })}
                                </Typography>

                                <br />

                                {templateLocales
                                    .map((loc) => <Typography type="tiny">{i18n.t(`locale.${loc}`)}</Typography>)}

                                <br />

                                <Typography type="tiny">
                                    {i18n.t('surveyWizard.surveyCreate.languageWarning2', {
                                        1: i === 2 ? 'survey' : 'template',
                                    })}
                                </Typography>
                            </div>
                        )}
                        {surveyOptions[i].selected && !showTemplateLangWarning && (
                            <Select
                                cssClass="option-select"
                                label="Language"
                                tooltipTitle={i18n.t('proSurvey.languageSelectionTitle')}
                                tooltipText={i18n.t('proSurvey.languageSelectionBody')}
                                options={clientLocales
                                    .map((loc) => ({ label: i18n.t(`locale.${loc}`), value: loc }))}
                                onBlur={wizardStore.setDefaultLocale}
                            />
                        )}
                        {R.pathOr(false, ['showBreaklink'], option) && !showTemplateLangWarning && (
                            <div className="breaklinks-wrapper">
                                <Checkbox
                                    label={i18n.t('sw_breaklinks')}
                                    name="breakLinks"
                                    defaultChecked={wizardStore.survey.breakLinks}
                                    onBlur={wizardStore.setBreakLinks}
                                />
                                <HoverInfo
                                    cssClass="hover-info w-450"
                                    desc={i18n.t('create.survey.wizard.tooltipText.breaklinks')}
                                    dir="right"
                                    href="https://kb.explori.com/knowledge/linked-surveys"
                                    link
                                >
                                    <i className="info-icon fa-solid fa-circle-question" />
                                </HoverInfo>
                            </div>
                        )}
                    </Panel>
                ) : null
            ))
        )
    }

    render() {
        const { wizardStore } = this.props

        if (wizardStore.surveyCreateLoading) return <Spinner />

        return (
            <div id="survey-create">
                <div className="survey-options">
                    {this.renderTemplates()}
                    {this.renderRapidTemplates()}
                </div>
                {this.renderBlankSurvey()}
            </div>
        )
    }
}
